/**
 * THE ADDRESS WE SEND A RENTER SO SHE CAN TELL US WHAT WE DO NOT KNOW.
 *
 * `/home/dt-designers-2br-condo-2119-k7m2qv`
 *
 * Three parts, and the Product Owner chose the shape on 2026-09-28 against two alternatives:
 *
 *   · THE HOME, IN WORDS. A renter forwards this to whoever she is moving with, and a link that
 *     says which home it is survives that. It is the home's nickname, never its street address —
 *     the address is not ours to publish to somebody who has not asked to rent yet (mtr#85).
 *   · HER LEAD'S NUMBER. The link is made FOR one enquiry, so her answers land on the lead she
 *     already has instead of opening a second one beside it. That was the whole defect: the
 *     website's form makes a new lead every time, so sending it to an Airbnb renter would have
 *     produced a duplicate and still no phone number on the original.
 *   · AND SIX CHARACTERS THAT ARE NOT DERIVED FROM EITHER.
 *
 * WHY THE SIX CHARACTERS EXIST, WHICH IS THE ONLY PART NOBODY ASKED FOR.
 *
 * Lead numbers count. `…-2119` and `…-2120` are both real, so without this a person who has one
 * link can type the next one and answer somebody else's form — putting a stranger's dates and
 * phone number onto a real renter's lead. The six characters are from the platform's CSPRNG and
 * mean nothing, so the number stays readable and guessing the number stops working.
 *
 * They are SIX rather than the four first proposed: four is about a million tries and six is about
 * a billion, for two characters nobody will notice. The alphabet leaves out `l i o 0 1` because
 * this is a string a person may read off one screen and type into another.
 *
 * NOT A TOKEN IN THE QUOTE'S SENSE. `/q/` carries 192 bits and identifies a frozen document worth
 * money. This identifies a form. The page behind it says nothing about the renter — not her name,
 * not her email, not her dates — so a guessed link shows a stranger a blank form with a home's
 * name on it, and that is the whole prize.
 */

import { PUBLIC_BASE } from '../quote/publicLink.js'

/**
 * Where a renter's enquiry form lives.
 *
 * NOT EXPORTED, and neither are the three helpers below. Everything in this file that nothing
 * outside it calls stays inside it — the gate added in mtr#124 asks exactly that question, and
 * answering it by widening the baseline would have been the first use of that escape hatch on the
 * day after it was built. `slugFromPath` repeats the literal rather than building a regex out of
 * this constant, because a regex assembled from a variable is harder to read than the one thing it
 * has to stay in step with; they are four lines apart and the test holds them together.
 */
const ENQUIRY_LINK_PATH = '/home/'

/** Six characters, 30 bits, out of an alphabet a person can read aloud. */
const KEY_LENGTH = 6
/* Shared with the short links we send (links/shortLink.js): letters a person can read aloud, no 0/o, 1/l/i. */
export const ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789'

/**
 * `crypto` is a parameter so a test can stand where the platform stands, and there is no fallback
 * to `Math.random` — the same rule as the quote's token. A key that looks random and is not fails
 * silently, forever, and nothing on any screen says so.
 */
export function newLinkKey(crypto = globalThis.crypto) {
  if (!crypto?.getRandomValues) {
    throw new Error('this browser has no secure random source, so it cannot make a link')
  }
  /*
    31 IS NOT A POWER OF TWO, so `byte % 31` would make the first nine letters likelier than the
    rest. At six characters that is worth almost nothing to somebody guessing — but "almost
    nothing" is a claim, and rejecting the bytes that would skew it is four lines and no claim.
    248 is 31 × 8, so every byte below it maps to a letter evenly and the rest are drawn again.
  */
  const CEILING = ALPHABET.length * Math.floor(256 / ALPHABET.length)
  let out = ''
  while (out.length < KEY_LENGTH) {
    for (const b of crypto.getRandomValues(new Uint8Array(KEY_LENGTH))) {
      if (b < CEILING && out.length < KEY_LENGTH) out += ALPHABET[b % ALPHABET.length]
    }
  }
  return out
}

/** A word a URL can hold: lowercase, letters and digits, hyphens between, nothing on the ends. */
const slugPart = (text) => String(text ?? '')
  .normalize('NFKD')
  .toLowerCase()
  /* An apostrophe is not a word break: `Designer's` is `designers`, never `designer-s`. */
  .replace(/['\u2018\u2019]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60)
  .replace(/-+$/g, '')

/** The number a person says out loud: `MTR-2119` → `2119`. */
const leadNumber = (reference) => (/(\d+)\s*$/.exec(String(reference ?? '')) ?? [])[1] ?? null

/**
 * The slug for one renter and one home, or null when either is missing.
 *
 * NULL RATHER THAN A SLUG WITH A HOLE IN IT. A link built from a lead with no reference would be
 * `dt-designers-2br-condo--k7m2qv`, which opens, looks right, and belongs to nobody.
 */
export function enquirySlug({ listingTitle, reference, key }) {
  const home = slugPart(listingTitle)
  const number = leadNumber(reference)
  if (!home || !number || !key) return null
  return `${home}-${number}-${key}`
}

export const enquiryLinkUrl = (slug) => `${PUBLIC_BASE}${ENQUIRY_LINK_PATH}${slug}`

/**
 * The slug in an address bar, or null.
 *
 * Strict about the alphabet for the same reason the quote's is: a path that is nearly a slug is a
 * truncated paste, and saying so at once is better than a database round trip that says it slower.
 */
export function slugFromPath(pathname) {
  const m = /^\/home\/([a-z0-9]+(?:-[a-z0-9]+)*)\/?$/.exec(String(pathname ?? ''))
  return m && m[1].length >= 8 ? m[1] : null
}
